import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Button from '../UI/Button';
import { useNavigation } from '../../contexts/NavigationContext';

const FooterContainer = styled.footer`
  background: ${({ theme }) => theme.colors.background.secondary};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding: 4rem 0 2rem;
  position: relative;
  overflow: hidden;
`;

const FooterBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(155, 89, 182, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(155, 89, 182, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
  animation: footerGrid 30s linear infinite;
  
  @keyframes footerGrid {
    0% { transform: translate(0, 0); }
    100% { transform: translate(40px, 40px); }
  }
`;

const FooterContent = styled.div`
  position: relative;
  z-index: 2;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 3rem;
  margin-bottom: 3rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FooterSection = styled(motion.div)`
  h3 {
    color: ${({ theme }) => theme.colors.primary.yellow};
    margin-bottom: 1.5rem;
    font-size: 1.25rem;
    font-weight: 600;
  }
  
  p, a {
    color: ${({ theme }) => theme.colors.text.secondary};
    text-decoration: none;
    line-height: 1.6;
    transition: color 0.3s ease;
    margin-bottom: 0.5rem;
    display: block;
  }
  
  a:hover {
    color: ${({ theme }) => theme.colors.primary.blue};
  }
`;

const LogoSection = styled(motion.div)`
  h2 {
    font-size: 2rem;
    font-weight: 800;
    color: ${({ theme }) => theme.colors.primary.yellow};
    font-family: ${({ theme }) => theme.fonts.mono};
    text-shadow: 0 0 20px rgba(255, 229, 0, 0.5);
    margin-bottom: 1rem;
  }
  
  p {
    color: ${({ theme }) => theme.colors.text.secondary};
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const SocialLink = styled(motion.a)`
  width: 45px;
  height: 45px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.2rem;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary.blue};
    color: ${({ theme }) => theme.colors.text.primary};
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 128, 214, 0.3);
  }
`;

const QuickLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const QuickLink = styled.a`
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  padding: 0.5rem 0;
  border-bottom: 1px solid transparent;
  transition: all 0.3s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary.yellow};
    border-bottom-color: ${({ theme }) => theme.colors.primary.yellow};
    padding-left: 0.5rem;
  }
`;

const ContactInfo = styled.div`
  margin-bottom: 1rem;
  
  .contact-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    color: ${({ theme }) => theme.colors.text.secondary};
    
    .icon {
      font-size: 1.1rem;
      color: ${({ theme }) => theme.colors.primary.yellow};
      min-width: 20px;
    }
  }
`;

const NewsletterSection = styled.div`
  background: ${({ theme }) => theme.gradients.card};
  border-radius: 15px;
  padding: 2rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  backdrop-filter: blur(10px);
  
  h4 {
    color: ${({ theme }) => theme.colors.primary.yellow};
    margin-bottom: 1rem;
    font-size: 1.2rem;
    font-weight: 600;
  }
  
  p {
    color: ${({ theme }) => theme.colors.text.secondary};
    margin-bottom: 1.5rem;
    font-size: 0.95rem;
  }
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const EmailInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 0.9rem;
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.blue};
    box-shadow: 0 0 0 3px rgba(0, 128, 214, 0.1);
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 0.9rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    text-align: center;
  }
`;

const LegalLinks = styled.div`
  display: flex;
  gap: 2rem;
  
  a {
    color: ${({ theme }) => theme.colors.text.muted};
    text-decoration: none;
    transition: color 0.3s ease;
    
    &:hover {
      color: ${({ theme }) => theme.colors.primary.blue};
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 1rem;
  }
`;
const LogoImage = styled.img`
  height: clamp(40px, 4vw, 80px);
  width: auto;
  transition: all 0.3s ease;
  filter: drop-shadow(0 0 10px rgba(255, 229, 0, 0.3));
  
  /* Minimum sizes for different breakpoints */
  // @media (min-width: 768px) {
  //   height: clamp(50px, 3.5vw, 70px);
  // }
  
  @media (min-width: 1440px) {
    height: clamp(60px, 4vw, 80px);
  }
  
  @media (min-width: 1920px) {
    height: clamp(70px, 3.5vw, 85px);
  }
  
  &:hover {
    transform: scale(1.05);
    filter: drop-shadow(0 0 15px rgba(255, 229, 0, 0.5));
  }
  
  /* Disable hover effects on touch devices */
  @media (hover: none) {
    &:hover {
      transform: none;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: clamp(40px, 25vw, 200px);
  }
`;

const Footer = () => {
  const { navigateTo } = useNavigation();
  const socialLinks = [
    { icon: '🐦', href: 'https://twitter.com/Bsides_ABQ', label: 'Twitter' },
    { icon: '💼', href: 'https://linkedin.com/company/bsides-albuquerque', label: 'LinkedIn' },
    { icon: '📧', href: 'mailto:info@bsidesabq.org', label: 'Email' },
  ];

  const quickLinks = [
    { label: 'About Event', href: '#about' },
    { label: 'Speakers', href: '#speakers' },
    { label: 'Event Details', href: '#event' },
    { label: 'Sponsor Us', href: '#sponsors' }
  ];

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    // Handle newsletter subscription
    console.log('Newsletter subscription submitted');
  };

  const scrollToSection = (href) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <FooterContainer id="contact">
      <FooterBackground />
      <div className="container">
        <FooterContent>
          <FooterGrid>
            
            <LogoSection
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <LogoImage 
              src="/bsidesabq.webp" 
              alt="BSides Albuquerque Logo"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
              {/* <h2>Security BSides Albuquerque</h2> */}
              <p>
                A community-driven cybersecurity conference bringing together security 
                professionals, researchers, and enthusiasts in New Mexico.
              </p>
              <SocialLinks>
                {socialLinks.map((social, index) => (
                  <SocialLink
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    {social.icon}
                  </SocialLink>
                ))}
              </SocialLinks>
            </LogoSection>

            <FooterSection
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3>Event Details</h3>
              <p><strong>Date:</strong> July 25-26, 2025</p>
              <p><strong>Location:</strong> Albuquerque, New Mexico</p>
              <p><strong>Venue:</strong> UNM Continuing Education - Conference Center</p>
              <p><strong>Registration:</strong> $0 - $23.18</p>
            </FooterSection>

            <FooterSection
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3>Quick Links</h3>
              <QuickLinks>
                {quickLinks.map((link) => (
                  <QuickLink
                    key={link.label}
                    href={link.href}
                    onClick={(e) => {
                      if (link.href.startsWith('#')) {
                        e.preventDefault();
                        scrollToSection(link.href);
                      }
                    }}
                  >
                    {link.label}
                  </QuickLink>
                ))}
              </QuickLinks>
            </FooterSection>

            <FooterSection
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3>Contact Info</h3>
              <ContactInfo>
                <div className="contact-item">
                  <span className="icon">📧</span>
                  <a href="mailto:info@bsidesabq.org">info@bsidesabq.org</a>
                </div>
                <div className="contact-item">
                  <span className="icon">🐦</span>
                  <a href="https://twitter.com/Bsides_ABQ" target="_blank" rel="noopener noreferrer">
                    @BSides_ABQ
                  </a>
                </div>
                <div className="contact-item">
                  <span className="icon">💼</span>
                  <a href="https://www.linkedin.com/company/bsides-albuquerque" target="_blank" rel="noopener noreferrer">
                    BSides Albuquerque
                  </a>
                </div>
                {/* <div className="contact-item">
                  <span className="icon">💬</span>
                  <a href="https://discord.gg/bsidesabq" target="_blank" rel="noopener noreferrer">
                    Join our Discord
                  </a>
                </div> */}
              </ContactInfo>
            </FooterSection>
          </FooterGrid>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <NewsletterSection>
              <h4>Stay Updated</h4>
              <p>Get the latest news about Security BSides Albuquerque, speaker announcements, and cybersecurity insights.</p>
              <NewsletterForm onSubmit={handleNewsletterSubmit}>
                <EmailInput 
                  type="email" 
                  placeholder="Enter your email" 
                  required 
                />
                <Button variant="primary" type="submit">
                  Subscribe
                </Button>
              </NewsletterForm>
            </NewsletterSection>
          </motion.div>

          <FooterBottom>
            <div>
              <p>&copy; 2025 Security BSides Albuquerque. All rights reserved.</p>
              {/* <p>Part of the Hacking Village | <a href="https://www.cyberskillslabs.org/" target="_blank" rel="noopener noreferrer">SecurityBSides.com</a></p> */}
            </div>
            <LegalLinks>
              <a href="/privacy" onClick={(e) => { e.preventDefault(); navigateTo('privacy'); }}>Privacy Policy</a>
              <a href="/codeofconduct" onClick={(e) => { e.preventDefault(); navigateTo('codeofconduct'); }}>Code Of Conduct</a>
              <a href="/terms" onClick={(e) => { e.preventDefault(); navigateTo('terms'); }}>Terms & Conditions</a>
            </LegalLinks>
          </FooterBottom>
        </FooterContent>
      </div>
    </FooterContainer>
  );
};

export default Footer;